import React, { useContext, useEffect, useRef, useState } from 'react';
import ApiServices from '../../service/api/ApiServices';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { CommonBodyForTable, commonCloseDialogFooter, srBodyTemplate } from '../Common/CommonComponents';
import { barDataForBarChart, DataForpieChart } from '../Common/CommonFunctions';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { managementCheck } from '../../service/admin/superadmin';
import { Dropdown } from 'primereact/dropdown';
import { permissionContext } from "../../AppWrapper";

const apiServices = new ApiServices();
const saved = localStorage.getItem("user");
const initialValue = JSON.parse(saved);

// Colors for the 5 chart-eligible stats
const colorData = ['#F87171', '#FB923C', '#FBBF24', '#A78BFA', '#60A5FA'];
// Distinct colors for the two stat-only cards
const TOTAL_MACHINES_COLOR = '#0EA5E9';
const CUP_STUCK_COLOR = '#F43F5E';
const WATER_LOW_THRESHOLD = 40;
const CANISTER_EMPTY_THRESHOLD = 20;

const getWaterLevelBool = (m) => {
  if (typeof m?.waterLevelLow === 'boolean') {
    return m.waterLevelLow;
  }
  const w = m?.water;
  if (typeof w === 'number') {
    return w < WATER_LOW_THRESHOLD;
  }
  return false;
};
const getRefillRequired = (m) => {
  const r = m?.refill;
  if (r && typeof r === 'object') return r.required === true;
  return r === true;
};
// Handles total_machines being either a plain number (current backend response)
// or a { count, percentage } object (a shape you've sent before) — normalize both.
const getTotalMachinesCount = (val) => {
  if (typeof val === 'number') return val;
  if (val && typeof val === 'object') return val.count ?? 0;
  return 0;
};
const getTotalMachinesPercentage = (val) => {
  if (val && typeof val === 'object' && typeof val.percentage === 'number') return val.percentage;
  return null;
};
// Only the 5 stats that also drive the bar/pie charts.
// canister_empty removed entirely from cards/charts (backend may still send it, we just ignore it).
// total_machines and cup_stuck are handled separately (stat-card only, not charted).
const STATUS_META = [
  { key: 'cup_tray_empty', title: 'Cup Tray Empty', test: (m) => m.cupTrayEmpty === true, icon: 'pi pi-inbox' },
  { key: 'refill_required', title: 'Refill Required', test: (m) => getRefillRequired(m), icon: 'pi pi-refresh' },
  { key: 'water_level_low', title: 'Water Level Low', test: (m) => getWaterLevelBool(m), icon: 'pi pi-exclamation-circle' },
  { key: 'machine_online', title: 'Machine Online', test: (m) => m.status === 'ONLINE', icon: 'pi pi-check-circle' },
  { key: 'machine_offline', title: 'Machine Offline', test: (m) => m.status === 'OFFLINE', icon: 'pi pi-times-circle' },
].map((item, index) => ({ ...item, color: colorData[index] }));
const lowCanistersTemplate = (rowData) => {
  const low = (rowData.canisters || []).filter((c) => c.level < CANISTER_EMPTY_THRESHOLD);
  if (low.length === 0) return '-';
  return low.map((c) => `${c.name} (${c.level}%)`).join(', ');
};
const trueFalseTemplate = (fieldOrFn) => (rowData) => {
  const val = typeof fieldOrFn === 'function' ? fieldOrFn(rowData) : rowData[fieldOrFn];
  return val ? 'True' : 'False';
};
const yesNoTemplate = (field) => (rowData) => (rowData[field] ? 'Yes' : 'No');
const lastSeenTemplate = (rowData) =>
  rowData.lastDataReceivedAt ? new Date(rowData.lastDataReceivedAt).toLocaleString('en-IN') : '-';
const cupStuckDateTemplate = (rowData) =>
  rowData.date ? new Date(rowData.date).toLocaleString('en-IN') : '-';

const accounts = [
  {
    name: 'CIPL(Demo)',
    userid: 'ld-em890hwskc0cibe6',
    dropDownLabel: 'CIPL(Demo)'
  },
  {
    name: 'IOCL',
    userid: 'usr-au6tt4oxmtjz1vum',
    dropDownLabel: 'IOCL'
  }
];

export const AutodispenserAnalytics = () => {
  const rolePermissions = useContext(permissionContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalMachines, setTotalMachines] = useState(0);
  const [machines, setMachines] = useState([]);
  const [cupStuckArray, setCupStuckArray] = useState([]);
  // chartMasterData: ONLY the 5 chart-eligible stats (drives bar + pie)
  const [chartMasterData, setChartMasterData] = useState(null);
  const [labelsData, setLabelsData] = useState(null);
  const [valuesData, setValuesData] = useState(null);
  // allStats: everything shown as KPI cards (Total Machines + 5 stats + Cup Stuck)
  const [allStats, setAllStats] = useState(null);
  const [detailedDialog, setDetailedDialog] = useState(false);
  const [selectedBar, setSelectedBar] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const dt = useRef(null);

  useEffect(() => {
    getReport();
  }, []);

  const getReport = async (selectedUserid = null) => {
    setLoading(true);
    setError(false);
    try {
      const userid = initialValue?.userid;
      const userCheck = [
        'usr-em890k81kypncp79', 'usr-4kcz06434k16hr64q', 'usr-em8907y3leqy5c8v',
        'usr-4kcz067ppk1bogdzy', 'usr-em890mjolcq0j6sy', 'usr-au6t1y10wmk0y8763'
      ];
      const isMatchedUser = userCheck.includes(userid);
      const isManagement = managementCheck();
      let payload;
      if (initialValue?.userType === 'enterprise') {
        payload = { userid: initialValue?.userOwner };
      } else if (isMatchedUser || isManagement || initialValue?.userType === 'owner' || initialValue?.userType === 'operation') {
        payload = selectedUserid
          ? { userid: selectedUserid }
          : {};
      } else {
        payload = {
          userid: selectedUserid || userid
        };
      }

      const resdata = await apiServices.autodispenserAnalytics(payload);
      const body = resdata?.data;
      const data = body?.data;
      if (!body || body.success === false || !data) {
        setError(true);
        setLoading(false);
        return;
      }
      const machineList = data.machines || [];
      setTotalMachines(getTotalMachinesCount(data.total_machines));
      setMachines(machineList);
      setCupStuckArray(data.cupStuckArray || []);
      // Chart-eligible stats only
      const master = STATUS_META.map((meta) => ({
        ...meta,
        count: data[meta.key]?.count ?? 0,
        percentage: data[meta.key]?.percentage ?? 0,
      }));
      setChartMasterData(master);
      setLabelsData(master.map((x) => x.title));
      setValuesData(master.map((x) => x.count));
      // Stat-only cards
      const totalMachinesStat = {
        key: 'total_machines',
        title: 'Total Machines',
        icon: 'pi pi-th-large',
        color: TOTAL_MACHINES_COLOR,
        count: getTotalMachinesCount(data.total_machines),
        percentage: getTotalMachinesPercentage(data.total_machines),
        dataSource: 'machines',
        test: () => true, // clicking it shows every machine
      };
      const cupStuckStat = {
        key: 'cup_stuck',
        title: 'Cup Stuck',
        icon: 'pi pi-ban',
        color: CUP_STUCK_COLOR,
        count: data.cupStuckCount ?? 0,
        percentage: null, // no percentage provided for this stat
        dataSource: 'cupStuck',
      };
      setAllStats([totalMachinesStat, ...master, cupStuckStat]);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  const barData = barDataForBarChart(labelsData, valuesData, 'No. of Machines', colorData);
  const pieChartData = DataForpieChart(labelsData, valuesData, 'No. of Machines', colorData);
  const barOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `Count: ${ctx.parsed.y}`,
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'top',
        color: '#1f2937',
        font: { weight: 'bold', size: 13 },
        formatter: (value) => value,
        clip: false,
        clamp: true,
      },
    },
    layout: {
      padding: { top: 30 },
    },
    datasets: {
      bar: {
        barPercentage: 0.5,
        categoryPercentage: 0.6,
      },
    },
    scales: {
      x: {
        ticks: { color: '#495057' },
        grid: { color: 'rgba(160, 167, 181, .3)' },
        title: { display: true, text: 'Machine Status' },
      },
      y: {
        ticks: { color: '#495057', precision: 0 },
        grid: { color: 'rgba(160, 167, 181, .3)' },
        title: { display: true, text: 'No. of Machines' },
        suggestedMax: (Math.max(...(valuesData || [0])) || 0) + 1,
      },
    },
    onClick: (e, clickedElement) => getBarData(clickedElement),
  };
  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.parsed}`,
        },
      },
      datalabels: {
        color: '#fff',
        font: { weight: 'bold', size: 11 },
        textAlign: 'center',
        formatter: (value, ctx) => {
          if (!value) return null;
          const dataset = ctx.chart.data.datasets[0].data;
          const total = dataset.reduce((a, b) => a + b, 0);
          const pct = total ? ((value / total) * 100).toFixed(1) : 0;
          if (Number(pct) < 5) return null; // Hide text if slice is too small to prevent overflow
          return `${value}\n${pct}%`;
        },
      },
    },
    onClick: (e, clickedElement) => getBarData(clickedElement),
  };
  // Chart click still indexes into chartMasterData (the 5 chart-only stats)
  const getBarData = (clicked) => {
    if (clicked.length > 0) {
      openDetail(chartMasterData[clicked[0].index]);
    }
  };
  const openDetail = (stat) => {
    setSelectedBar(stat);
    if (stat.dataSource === 'cupStuck') {
      setDetailedData(cupStuckArray);
    } else if (stat.test) {
      setDetailedData(machines.filter(stat.test));
    } else {
      setDetailedData(machines);
    }
    setDetailedDialog(true);
  };
  const hideDetailed = () => setDetailedDialog(false);
  const sensorFooter = commonCloseDialogFooter('Close', hideDetailed);
  const exportCSV = () => dt.current.exportCSV();
  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <h6 className="p-mt-2">{selectedBar?.title} — Details</h6>
      </span>
      <span className="p-input-icon-right">
        <div className="p-grid">
          <div className="p-col-10"></div>
          <div className="p-col-2">
            <Button icon="pi pi-download" className="p-ml-100 custom-group-btn p-button-rounded" onClick={exportCSV} />
          </div>
        </div>
      </span>
    </div>
  );
  if (loading) {
    return (
      <div className="card">
        <div className="spinner-div">
          <center><ProgressSpinner strokeWidth="7" animationDuration="4s" /></center>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Something went wrong while loading machine analytics.</p>
          <p style={{ color: 'red' }}> {error} </p>
          <Button label="Retry" icon="pi pi-refresh" className="custom-group-btn" onClick={getReport} />
        </div>
      </div>
    );
  }

  const isCupStuckView = selectedBar?.dataSource === 'cupStuck';

  const changeAccount = (e) => {
    setSelectedAccount(e.value);
    getReport(e.value?.userid);
  };

  return (
    <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '16px' }}>
      {(rolePermissions.machineStatusOverviewFilter || initialValue?.userType === 'owner') && <div className="card" style={{ marginBottom: '15px' }}>
        <div className="p-inputgroup ">
          <Button className='custom-group-btn' label="ACC" />
          <Dropdown
            value={selectedAccount}
            onChange={changeAccount}
            options={accounts}
            optionLabel="dropDownLabel"
            placeholder="Select Account"
            filter
            className="p-m-0 multiselect-custom"
            showClear
          />
        </div>
      </div>}
      {!totalMachines ? <div className="card">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>No machine data available yet.</p>
        </div>
      </div> : <>{/* Premium KPI Stat Cards Grid - includes Total Machines + Cup Stuck, which are NOT in the charts */}
        <div className="p-grid" style={{ marginBottom: '10px' }}>
          {allStats.map((s) => (
            <div className="p-col-12 p-md-4 p-lg-2" key={s.key}>
              <div
                onClick={() => openDetail(s)}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                  border: '1px solid #e2e8f0',
                  borderLeft: `5px solid ${s.color || '#3b82f6'}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.03)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {s.title}
                  </span>
                  <div style={{ backgroundColor: `${s.color}15`, padding: '8px', borderRadius: '10px', color: s.color }}>
                    <i className={s.icon} style={{ fontSize: '16px' }}></i>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '28px', fontWeight: '700', color: '#0f172a' }}>{s.count}</span>
                  {s.percentage != null && (
                    <span style={{ fontSize: '13px', fontWeight: '600', color: s.color, backgroundColor: `${s.color}15`, padding: '2px 8px', borderRadius: '6px' }}>
                      {s.percentage}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-grid p-jc-around">
          <div className="p-col-12" style={{ width: '64%' }}>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
              <h5>Machine Status Overview - Bar Chart</h5>
              <Chart type="bar" data={barData} options={barOptions} plugins={[ChartDataLabels]} />
            </div>
          </div>
          <div className="p-col-12" style={{ width: '32%' }}>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
              <h5>Machine Status Overview - Pie Chart</h5>
              <Chart type="pie" data={pieChartData} options={pieOptions} plugins={[ChartDataLabels]} />
            </div>
          </div>
        </div>
        {/* Detailed Report Dialog */}
        <Dialog className="p-fluid" header="Detailed Report" visible={detailedDialog} style={{ width: '70vw' }} footer={sensorFooter} onHide={hideDetailed} maximizable={true} blockScroll={true}>
          <div className="p-grid crud-demo">
            <div className="p-col-12">
              <div className="card">
                {isCupStuckView ? (
                  <DataTable
                    ref={dt}
                    value={detailedData}
                    dataKey="_id"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    className="datatable-responsive"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                    emptyMessage="No cup-stuck records found."
                    exportFilename="Cup-Stuck-Report"
                    header={header}
                  >
                    <Column field="srid" header="Sr.No." body={srBodyTemplate} style={{ width: '4rem' }} exportable={false}></Column>
                    <Column field="machineId" header="Machine ID" body={CommonBodyForTable} sortable></Column>
                    <Column field="userid" header="User ID" body={CommonBodyForTable} sortable></Column>
                    <Column field="cupStuckCount" header="Cup Stuck Count" body={CommonBodyForTable} sortable></Column>
                    <Column header="Date" body={cupStuckDateTemplate} sortable></Column>
                  </DataTable>
                ) : (
                  <DataTable
                    ref={dt}
                    value={detailedData}
                    dataKey="machineId"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    className="datatable-responsive"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                    emptyMessage="No machines found."
                    exportFilename={`${selectedBar?.title || 'Machine'}-Report`}
                    header={header}
                  >
                    <Column field="srid" header="Sr.No." body={srBodyTemplate} style={{ width: '4rem' }} exportable={false}></Column>
                    <Column field="machineId" header="Machine ID" body={CommonBodyForTable} sortable></Column>
                    <Column field="status" header="Status" body={CommonBodyForTable} sortable></Column>
                    <Column field="waterLevelLow" header="Water Level Low" body={trueFalseTemplate(getWaterLevelBool)} sortable></Column>
                    <Column field="cupTrayEmpty" header="Cup Tray Empty" body={yesNoTemplate('cupTrayEmpty')} sortable></Column>
                    <Column header="Refill Required" body={(r) => (getRefillRequired(r) ? 'Yes' : 'No')} sortable></Column>
                    <Column header="Low Canisters" body={lowCanistersTemplate}></Column>
                    <Column header="Last Seen" body={lastSeenTemplate} sortable></Column>
                  </DataTable>
                )}
              </div>
            </div>
          </div>
        </Dialog></>}
    </div>
  );
};
export default AutodispenserAnalytics;